import { Button } from '@repo/ui'
import { MessageSquareText, RotateCw } from 'lucide-react'
import type { ReactNode } from 'react'

// Where the open document started, and a way back to it. Rendered only when that chat is
// not the one already open beside the canvas, because the reader is asking why these two
// things are adjacent and "they are the same conversation" is not a surprise worth a row.
// The caller decides: an artifact created outside a conversation has no origin, and one
// outside the rail's window has none loaded, so absence covers three cases at once.
export type CanvasProvenance = { chatTitle: string; onOpenChat: () => void }

export function CanvasProvenanceLine({ provenance }: { provenance: CanvasProvenance }) {
  return (
    <div
      data-testid="panel-provenance"
      className="mt-1 flex items-center gap-1.5 text-[0.6875rem] text-muted-foreground"
    >
      <MessageSquareText className="size-3 shrink-0" aria-hidden="true" />
      <span className="min-w-0 truncate">From {provenance.chatTitle}</span>
      <Button
        type="button"
        variant="link"
        size="sm"
        // The visible words have to survive into the announced name, or a speech-input
        // reader saying "click Open chat" matches nothing. The title is appended rather
        // than substituted.
        aria-label={`Open chat ${provenance.chatTitle}`}
        className="h-auto shrink-0 p-0 text-[0.6875rem]"
        onClick={provenance.onOpenChat}
      >
        Open chat
      </Button>
    </div>
  )
}

// Sticky because the header is what says which scope this is, and a proposal opened at
// a section scrolls itself past the header on arrival: the label would be in the DOM and
// never on screen for the exact case it exists to explain. No z-index, so the panel's
// own close control (z-10, earlier in the tree) keeps painting above it.
export function PanelHeader({
  title,
  titleContent,
  icon,
  scope,
  provenance,
  details,
  actions,
  notice
}: {
  title: string
  // Artifact names are editable in place. Supplying the complete heading keeps that
  // control in the one canonical title slot without duplicating the title below.
  titleContent?: ReactNode
  icon?: ReactNode
  scope: string
  provenance?: CanvasProvenance
  // Stable identity facts belong with the title and scope, above transient actions.
  details?: ReactNode
  // Every control the panel offers, on a row of the header's own beneath the title. Beside
  // the title the row had a fixed capacity, because it neither wrapped nor shrank and the
  // corner it sat in belongs to the canvas' floating widen and close controls: a third
  // control there was paid for by the title first and ran under the close button after
  // that. Its own row has no capacity and no corner to reach, so what a panel offers is
  // no longer a question of how much of it fits.
  actions?: ReactNode
  // A further line for text that has to stay with a control the row holds
  // (CanvasExportRefusal), which wraps and so cannot share a row with controls that do
  // not. Inside the header because the header is sticky.
  notice?: ReactNode
}) {
  return (
    <header className="sticky top-0 border-b bg-background px-5 py-4">
      {/* pr-20 clears the canvas' floating widen/close cluster (two icon-sm buttons plus
          its top-right offset need about 76px). Only the title shares that line now, and
          it truncates, so the reservation is never contested. */}
      <div className="min-w-0 pr-20">
        <div className="flex items-center gap-2">
          {icon}
          <div className="min-w-0 flex-1">
            {titleContent ?? <h2 className="truncate text-sm font-semibold tracking-tight">{title}</h2>}
          </div>
        </div>
        <p data-testid="panel-scope" className="mt-1 truncate text-[0.6875rem] text-muted-foreground">
          {scope}
        </p>
        {provenance !== undefined && <CanvasProvenanceLine provenance={provenance} />}
      </div>
      {details !== undefined && <div className="mt-3">{details}</div>}
      {actions !== undefined && <div className="mt-3 flex flex-wrap items-center gap-1">{actions}</div>}
      {notice}
    </header>
  )
}

export function PanelFailure({ message, retry }: { message: string; retry?: () => void }) {
  return (
    <div className="px-5 py-8 text-sm" role="alert">
      <p className="text-muted-foreground">{message}</p>
      {retry !== undefined && (
        <Button type="button" variant="outline" size="sm" className="mt-4" onClick={retry}>
          <RotateCw className="size-4" aria-hidden="true" />
          Try again
        </Button>
      )}
    </div>
  )
}
